/**
 * Team Grid
 *
 * Grid layout for 3+ teams, displayed below the flow center area.
 * Auto-sizes columns based on team count.
 */

import type { QueueRankConfig } from "@rivaled/shared";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { PlayerCard } from "../components/PlayerCard";
import { TeamHeader } from "../components/TeamHeader";
import { useCurrentPlayerId } from "../hooks/useCurrentPlayerId";
import { getPartyColor } from "../lib/party-colors";
import type { PlayerStatusFn } from "./getNodePlayerStatus";
import type { TeamData } from "./types";

type TeamGridProps = {
  teams: TeamData[];
  myPartyId: string | null;
  rankConfig: QueueRankConfig | null;
  getPlayerStatus?: PlayerStatusFn;
};

export function TeamGrid({ teams, myPartyId, rankConfig, getPlayerStatus }: TeamGridProps) {
  const currentPlayerId = useCurrentPlayerId();

  // Responsive column count based on team count
  const gridCols =
    teams.length <= 3
      ? "grid-cols-1 @md:grid-cols-3"
      : teams.length === 4
        ? "grid-cols-2 @md:grid-cols-4"
        : "grid-cols-2 @md:grid-cols-3 @xl:grid-cols-4";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
      className={`@container grid ${gridCols} gap-3`}
    >
      {teams.map((team) => (
        <div
          key={team.teamIndex}
          className={cn(
            "rounded-lg border overflow-hidden",
            team.isMyTeam
              ? "bg-surface-800/60 border-brand-500/25 shadow-[0_0_12px_rgba(38,217,181,0.06)]"
              : "bg-surface-800/60 border-surface-700/30",
          )}
        >
          <TeamHeader teamName={team.teamName} isMyTeam={team.isMyTeam} />
          <div className="flex flex-col gap-1 p-2">
            {team.players.map((player, i) => {
              const status = getPlayerStatus?.(player.userId);
              return (
                <PlayerCard
                  key={player.userId}
                  player={player}
                  variant="compact"
                  isMe={player.userId === currentPlayerId}
                  partyColor={getPartyColor(player.partyId, myPartyId)}
                  rankConfig={rankConfig}
                  animationDelay={i * 0.05}
                  rightSlot={status?.rightSlot}
                  className={status?.className}
                />
              );
            })}
          </div>
        </div>
      ))}
    </motion.div>
  );
}
